import React from 'react';
import { getCustomer } from '../../../../../helpers/customer-helper';
import { getProject } from '../../../../../helpers/project-helper';
import {
    getTickets,
    searchTickets,
} from '../../../../../helpers/ticket-helper';
import {
    STATUS_GROUP_PENDING,
    STATUS_GROUP_RESOLVED,
    STATUS_GROUP_OVERDUE,
} from '../helpers';

export const useTaskListFiltersData = ({ token }) => {
    const [tickets, setTickets] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState('');

    const [searchField, setSearchField] = React.useState('code');
    const [searchTerm, setSearchTerm] = React.useState('');
    const [statusFilter, setStatusFilter] = React.useState('ALL');
    const [statusGroupFilter, setStatusGroupFilter] = React.useState('');
    const [typeFilter, setTypeFilter] = React.useState('ALL');
    const [customerFilter, setCustomerFilter] = React.useState('ALL');
    const [projectFilter, setProjectFilter] = React.useState('ALL');
    const [customerOptions, setCustomerOptions] = React.useState([]);
    const [projectOptions, setProjectOptions] = React.useState([]);
    const [limit, setLimit] = React.useState(10);
    const [offset, setOffset] = React.useState(0);
    const [pagination, setPagination] = React.useState({
        total: 0,
        hasNext: false,
        hasPrev: false,
    });

    const loadTickets = React.useCallback(async () => {
        if (!token) {
            setError('No se pudo validar la sesión.');
            setTickets([]);
            setPagination({ total: 0, hasNext: false, hasPrev: false });
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError('');

        const params = {
            limit,
            offset,
        };

        if (statusGroupFilter) {
            params.statusGroup = statusGroupFilter;
        } else if (statusFilter !== 'ALL') {
            params.status = statusFilter;
        }
        if (typeFilter !== 'ALL') {
            params.type = typeFilter;
        }
        if (customerFilter !== 'ALL') {
            params.customer = customerFilter;
        }
        if (projectFilter !== 'ALL') {
            params.project = projectFilter;
        }

        const normalizedTerm = String(searchTerm || '').trim();
        const resp = normalizedTerm
            ? await searchTickets(token, {
                ...params,
                field: searchField,
                value: normalizedTerm,
            })
            : await getTickets(token, params);

        if (!resp?.success) {
            setError(resp?.message || 'No se pudo obtener el listado de tickets.');
            setTickets([]);
            setPagination({ total: 0, hasNext: false, hasPrev: false });
            setIsLoading(false);
            return;
        }

        const rows = Array.isArray(resp.data) ? resp.data : [];
        setTickets(rows);
        setPagination({
            total: Number(resp.total || 0),
            hasNext: Boolean(resp.hasNext),
            hasPrev: Boolean(resp.hasPrev),
        });
        setIsLoading(false);
    }, [token, limit, offset, statusFilter, statusGroupFilter, typeFilter, customerFilter, projectFilter, searchField, searchTerm]);

    const loadFilterOptions = React.useCallback(async () => {
        if (!token) return;

        const [customersResp, projectsResp] = await Promise.all([
            getCustomer(token, { limit: 1000 }),
            getProject(token, { limit: 1000 }),
        ]);

        if (customersResp?.success) {
            const rows = Array.isArray(customersResp.data) ? customersResp.data : [];
            setCustomerOptions(rows.map((item) => ({
                value: item._id,
                label: item?.name || item?.code || 'Cliente',
            })));
        }

        if (projectsResp?.success) {
            const rows = Array.isArray(projectsResp.data) ? projectsResp.data : [];
            setProjectOptions(rows.map((item) => ({
                value: item._id,
                label: item?.name || item?.code || 'Proyecto',
            })));
        }
    }, [token]);

    React.useEffect(() => {
        loadTickets();
    }, [loadTickets]);

    React.useEffect(() => {
        loadFilterOptions();
    }, [loadFilterOptions]);

    const handleClearFilters = React.useCallback(() => {
        setSearchField('code');
        setSearchTerm('');
        setStatusFilter('ALL');
        setStatusGroupFilter('');
        setTypeFilter('ALL');
        setCustomerFilter('ALL');
        setProjectFilter('ALL');
        setOffset(0);
    }, []);

    const isDashboardCardActive = React.useCallback((cardKey) => {
        if (cardKey === 'TOTAL') {
            return statusFilter === 'ALL' && !statusGroupFilter;
        }

        if (cardKey === STATUS_GROUP_PENDING || cardKey === STATUS_GROUP_RESOLVED || cardKey === STATUS_GROUP_OVERDUE) {
            return statusGroupFilter === cardKey;
        }

        return statusGroupFilter === '' && statusFilter === cardKey;
    }, [statusFilter, statusGroupFilter]);

    const handleDashboardCardFilter = React.useCallback((targetFilter) => {
        setOffset(0);

        if (targetFilter !== 'TOTAL' && isDashboardCardActive(targetFilter)) {
            setStatusFilter('ALL');
            setStatusGroupFilter('');
            return;
        }

        if (targetFilter === 'TOTAL') {
            setStatusFilter('ALL');
            setStatusGroupFilter('');
            return;
        }

        if (targetFilter === STATUS_GROUP_PENDING || targetFilter === STATUS_GROUP_RESOLVED || targetFilter === STATUS_GROUP_OVERDUE) {
            setStatusFilter('ALL');
            setStatusGroupFilter(targetFilter);
            return;
        }

        setStatusGroupFilter('');
        setStatusFilter(targetFilter);
    }, [isDashboardCardActive]);

    return {
        tickets,
        setTickets,
        isLoading,
        error,
        setError,
        searchField,
        setSearchField,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        statusGroupFilter,
        setStatusGroupFilter,
        typeFilter,
        setTypeFilter,
        customerFilter,
        setCustomerFilter,
        projectFilter,
        setProjectFilter,
        customerOptions,
        projectOptions,
        limit,
        setLimit,
        offset,
        setOffset,
        pagination,
        loadTickets,
        handleClearFilters,
        isDashboardCardActive,
        handleDashboardCardFilter,
    };
};